import { createBrowserRouter } from "react-router-dom";
import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
  type Middleware,
  type Reducer,
  type StoreEnhancer,
} from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import createSagaMiddleware from "redux-saga";
import { all, type AllEffect, fork, type ForkEffect } from "redux-saga/effects";

import { getSentryReduxEnhancer } from "@eroad/monitoring/sentry";

import { isDevelopmentEnv } from "../env/isDevelopmentEnv";
import { isDefined } from "../utils/isDefined";
import { isEmpty } from "../utils/isEmpty";
import type { ConfiguredStore, ConfigureStoreParams, EroadStore, StoreDomain } from "./types";

/**
 * Crate a redux store with the given initial state, injected reducers and injected sagas.
 */
function configureStore({
  initialDomains = [],
  initialState = {},
  middlewares = [],
}: ConfigureStoreParams): ConfiguredStore<typeof sagaMiddleware> {
  const sagaMiddleware = createSagaMiddleware<{ router: ReturnType<typeof createBrowserRouter> }>();

  const domainsReducers = getDomainReducers(initialDomains);
  const domainsSagas = getDomainSagas(initialDomains);
  const storeEnhancer = getStoreEnhancer([...middlewares, sagaMiddleware]);

  const rootReducer = combineReducers(domainsReducers);

  const store = createStore(rootReducer, initialState, storeEnhancer) as EroadStore;

  store.asyncReducers = {};
  store.injectReducer = (key, asyncReducer) => {
    if (store.asyncReducers[key] || domainsReducers[key]) {
      return;
    }

    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(combineReducers({ ...domainsReducers, ...store.asyncReducers }));
  };

  store.asyncSagas = {};
  store.injectSaga = (key, asyncSaga) => {
    if (!isDefined(asyncSaga) || store.asyncSagas[key] || domainsReducers[key]) {
      return;
    }

    store.asyncSagas[key] = asyncSaga;
    sagaMiddleware.run(asyncSaga);
  };

  store.injectInitialDomains = (initialDomains) => {
    initialDomains.forEach(({ key, reducer, sagaWatcher }) => {
      if (!isDefined(key)) {
        return;
      }

      if (isDefined(reducer)) {
        Object.values(reducer).forEach((reducer) => {
          store.injectReducer(key, reducer);
        });
      }

      if (isDefined(sagaWatcher)) {
        store.injectSaga(key, sagaWatcher);
      }
    });
  };

  sagaMiddleware.run(domainsSagas);

  return { sagaMiddleware, store };
}

/**
 * @internal
 */
function getDomainReducers(domains: StoreDomain[]): Record<string, Reducer> {
  if (isEmpty(domains)) {
    return {
      // To prevent redux from throwing an error when no reducers are provided.
      __DO_NOT_USE_emptyReducer: () => ({}),
    };
  }

  const reducers: Record<string, Reducer> = {};

  domains.forEach((domain) => {
    const domainReducers = domain.reducer;

    Object.entries(domainReducers).forEach(([key, reducer]) => {
      reducers[key] = reducer;
    });
  });

  return reducers;
}

/**
 * @internal
 */
function getDomainSagas(domains: StoreDomain[]): () => Generator<AllEffect<ForkEffect<any>>, void, unknown> {
  const sagas = domains
    .map((domain) => domain.sagaWatcher)
    .filter(isDefined)
    .map((saga) => fork(saga));

  return function* () {
    yield all(sagas);
  };
}

/**
 * @internal
 */
function getStoreEnhancer(middlewares: Middleware[]): StoreEnhancer {
  const middlewareEnhancer = applyMiddleware(...middlewares);
  const sentryReduxEnhancer = getSentryReduxEnhancer();
  const enhancers = [sentryReduxEnhancer?.(), middlewareEnhancer].filter(isDefined);

  if (isDevelopmentEnv()) {
    return composeWithDevTools(...enhancers);
  }

  return compose(...enhancers);
}

export { configureStore };
